import Fuse from 'fuse.js';
import { produce } from 'immer';
import { ampli } from 'lib-shared-tracking';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDebounce } from 'react-use';

import { Template, templatesApi } from 'apis/templates';
import { usePaginatedEndpoint } from 'utils/apis/pagination/usePaginatedEndpoint';

export enum QuickReplyType {
  Recommendations = 'recommendations',
  MostUsed = 'mostUsed',
  All = 'All',
  Favorites = 'favorites',
}

type QuickReplyListData = {
  items: readonly EnhancedTemplate[];
  isLoading: boolean;
  isError: boolean;
};

type QuickReplyListDataPaginated = QuickReplyListData & {
  hasNextPage: boolean;
  nextPage: () => void;
};

type QuickReplyData = {
  recommendedTemplates: QuickReplyListData;
  mostUsedTemplates: QuickReplyListData;
  favoriteTemplates: QuickReplyListData;
  allTemplates: QuickReplyListDataPaginated;
  toggleFavorite: (template: Template, locale: string) => void;
};

type OptimisticFavoriteState = {
  [key: Template['id']]: {
    favorite: boolean;
    updatedAt: Date;
    locale: string;
  };
};

export interface EnhancedTemplate extends Template {
  sourceList:
    | 'recommendations'
    | 'most_used'
    | 'templates'
    | 'all'
    | 'favorites';
  score?: number;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isEnhancedTemplate = (tpl: any): tpl is EnhancedTemplate => {
  return (
    tpl &&
    typeof tpl === 'object' &&
    'sourceList' in tpl &&
    'score' in tpl &&
    'id' in tpl &&
    'name' in tpl &&
    'content' in tpl &&
    'favorite' in tpl
  );
};

const enhanceTemplate =
  (
    sourceList: EnhancedTemplate['sourceList'],
    scores?: Record<string, number>,
  ) =>
  (template: Template): EnhancedTemplate => {
    const score = scores?.[template.id];
    return {
      ...template,
      sourceList,
      score,
    };
  };

export const useQuickReplyData = (
  types: QuickReplyType[],
  locale: string,
  csid: string,
  searchTermAll: string,
  searchTermFavorites: string,
): QuickReplyData => {
  const [favoriteState, setFavoriteState] = useState<OptimisticFavoriteState>(
    {},
  );
  const [favoriteTemplate] = templatesApi.useFavoriteTemplateMutation();
  const [unfavoriteTemplate] = templatesApi.useUnfavoriteTemplateMutation();

  const computeFavoriteStatus = useCallback(
    (template: Template) => {
      const state = favoriteState[template.id];
      if (
        state &&
        state.locale === locale &&
        (!template.last_edited_at ||
          state.updatedAt.getTime() >
            new Date(template.last_edited_at).getTime())
      ) {
        return {
          ...template,
          favorite: favoriteState[template.id].favorite,
        };
      }

      return template;
    },
    [favoriteState, locale],
  );

  const localSearchFilter = useCallback(
    (searchTerm: string, templates: Template[]) => {
      if (!searchTerm || !searchTerm.trim()) {
        return templates;
      }

      const fuse = new Fuse(templates, {
        keys: ['name'],
        threshold: 0.2,
        distance: 200,
      });

      return fuse.search(searchTerm).map(({ item }) => item);
    },
    [],
  );

  const mostUsedResults = templatesApi.useReadMostUsedQuery(
    { locale },
    {
      skip: !types.includes(QuickReplyType.MostUsed),
      selectFromResult: (data) => {
        return produce(data, (draft) => {
          if (!draft.data?.data.templates) return;
          draft.data.data.templates = draft.data?.data.templates.map(
            computeFavoriteStatus,
          );
        });
      },
    },
  );

  const allResults = usePaginatedEndpoint({
    endpoint: templatesApi.endpoints.readAllTemplatesPaginated,
    initialArg: {
      type: 'quick_response',
      locale,
      sort: { key: 'name', direction: 'asc' },
    },
    itemsSelector: ({ data }) => {
      return data.templates;
    },
    infinite: true,
  });

  const favoriteResults = templatesApi.useReadFavoritesQuery(
    { locale },
    {
      skip: !types.includes(QuickReplyType.Favorites),
      selectFromResult: (data) => {
        return produce(data, (draft) => {
          if (!draft.data?.data.templates) return;
          draft.data.data.templates = draft.data?.data.templates.map(
            computeFavoriteStatus,
          );
        });
      },
    },
  );

  const recommendationsResults = templatesApi.useReadRecommendationsQuery(
    {
      csid,
      locale,
    },
    {
      skip: !types.includes(QuickReplyType.Recommendations),
      selectFromResult: (data) => {
        return produce(data, (draft) => {
          if (!draft.data?.data.templates) return;
          draft.data.data.templates = draft.data?.data.templates.map(
            computeFavoriteStatus,
          );
        });
      },
    },
  );

  useEffect(() => {
    if (
      types.includes(QuickReplyType.All) &&
      allResults.arg?.locale !== locale
    ) {
      allResults.changeArg({
        type: 'quick_response',
        searchTerm: searchTermAll,
        locale,
        offset: 0,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [types, locale]);

  useDebounce(
    () => {
      if (
        types.includes(QuickReplyType.All) &&
        allResults.arg?.searchTerm !== searchTermAll
      ) {
        allResults.changeArg({
          type: 'quick_response',
          searchTerm: searchTermAll,
          locale,
          offset: 0,
        });
      }
    },
    300,
    [searchTermAll],
  );

  const toggleFavorite = useCallback(
    (template: Template, locale: string) => {
      setFavoriteState((prev) => ({
        ...prev,
        [template.id]: {
          favorite: !template.favorite,
          updatedAt: new Date(),
          locale,
        },
      }));
      if (template.favorite) {
        unfavoriteTemplate({ id: template.id, locale });
        ampli.quickReplyTemplateFavoriteToggled({ favorited: false });
      } else {
        favoriteTemplate({ id: template.id, locale });
        ampli.quickReplyTemplateFavoriteToggled({ favorited: true });
      }
    },
    [favoriteTemplate, unfavoriteTemplate],
  );

  return useMemo(
    () => ({
      recommendedTemplates: {
        items:
          recommendationsResults.data?.data.templates.map(
            enhanceTemplate(
              'recommendations',
              recommendationsResults.data?.data.scores,
            ),
          ) || [],
        isLoading:
          recommendationsResults.isLoading || recommendationsResults.isFetching,
        isFetching: recommendationsResults.isFetching,
        isError: recommendationsResults.isError,
      },
      mostUsedTemplates: {
        items:
          mostUsedResults.data?.data.templates.map(
            enhanceTemplate('most_used'),
          ) || [],
        isLoading: mostUsedResults.isLoading || mostUsedResults.isFetching,
        isFetching: mostUsedResults.isFetching,
        isError: mostUsedResults.isError,
      },
      favoriteTemplates: {
        items: localSearchFilter(
          searchTermFavorites,
          favoriteResults.data?.data.templates || [],
        ).map(enhanceTemplate('favorites')),
        isLoading: favoriteResults.isLoading || favoriteResults.isFetching,
        isFetching: favoriteResults.isFetching,
        isError: favoriteResults.isError,
      },
      allTemplates: {
        items: (allResults.items || [])
          .map(computeFavoriteStatus)
          .map(enhanceTemplate('all')),
        // The paginated hook does not expose a separate loading and fetching state
        isLoading: allResults.asyncStatus === 'pending',
        isError: allResults.asyncStatus === 'rejected',
        hasNextPage: allResults.hasNextPage,
        nextPage: allResults.nextPage,
      },
      toggleFavorite,
    }),
    [
      allResults.asyncStatus,
      allResults.hasNextPage,
      allResults.items,
      allResults.nextPage,
      computeFavoriteStatus,
      favoriteResults.data?.data.templates,
      favoriteResults.isError,
      favoriteResults.isFetching,
      favoriteResults.isLoading,
      localSearchFilter,
      mostUsedResults.data?.data.templates,
      mostUsedResults.isError,
      mostUsedResults.isFetching,
      mostUsedResults.isLoading,
      recommendationsResults.data?.data.scores,
      recommendationsResults.data?.data.templates,
      recommendationsResults.isError,
      recommendationsResults.isFetching,
      recommendationsResults.isLoading,
      searchTermFavorites,
      toggleFavorite,
    ],
  );
};
